import { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import {
  CLIP_FILTERS,
  TRANSITIONS,
  audioMaxDur,
  clamp,
  filterCss,
  fmt,
  r1,
} from './timeline';
import { AudioItem, Clip, TimelineLayout, VideoEditorApi, VideoLayer } from '../../../types/videoType';

type StillSource = HTMLImageElement | HTMLCanvasElement;

const frames = new Map<string, Promise<StillSource | null>>(); // `${url}@${time}` -> Promise<CanvasImageSource>

/** One still of a video (or the photo itself), cached. */
function still(url: string, time: number, image?: boolean): Promise<StillSource | null> {
  const key = `${url}@${image ? 0 : Math.round(time * 2) / 2}`;
  if (!frames.has(key)) {
    frames.set(
      key,
      new Promise<StillSource | null>((resolve) => {
        if (image) {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = url;
          return;
        }
        const v = document.createElement('video');
        v.muted = true;
        v.preload = 'auto';
        v.playsInline = true;
        v.onloadeddata = () => { v.currentTime = Math.min(time, Math.max(0, v.duration - 0.1)); };
        v.onseeked = () => {
          const c = document.createElement('canvas');
          c.width = 96;
          c.height = Math.round(96 * (v.videoHeight / v.videoWidth || 1));
          try {
            const ctx = c.getContext('2d');
            if (ctx) ctx.drawImage(v, 0, 0, c.width, c.height);
            resolve(c);
          } catch {
            resolve(null);
          }
          v.removeAttribute('src');
          v.load();
        };
        v.onerror = () => resolve(null);
        v.src = url;
      }),
    );
  }
  return frames.get(key)!;
}

interface ClipThumbProps {
  clip: Clip;
  width: number;
  height: number;
}

function ClipThumb({ clip, width, height }: ClipThumbProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let stop = false;
    still(clip.url, clip.trimStart + 0.3, clip.image).then((img) => {
      const c = ref.current;
      if (!c || stop || !img) return;
      c.width = Math.max(1, Math.round(width));
      c.height = height;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const src = img as StillSource & { naturalWidth?: number; naturalHeight?: number };
      const iw = src.naturalWidth || src.width;
      const ih = src.naturalHeight || src.height;
      const w = Math.max(24, (height * iw) / ih);
      ctx.filter = filterCss(clip.filter);
      for (let x = 0; x < c.width; x += w) ctx.drawImage(img, x, 0, w, height);
    });
    return () => { stop = true; };
  }, [clip.url, clip.trimStart, clip.image, clip.filter, width, height]);
  return (
    <canvas
      ref={ref}
      className="absolute top-1 bottom-1 left-0 right-0 w-full h-12 rounded-lg pointer-events-none"
      aria-hidden="true"
    />
  );
}

interface VideoTimelineProps {
  /** `lay` is narrowed to the richer TimelineLayout shape this component relies on. */
  ed: Omit<VideoEditorApi, 'lay'> & { lay: TimelineLayout };
  t: number;
  playing: boolean;
  onToggle: () => void;
  onSplit: () => void;
}

type DragState = Record<string, any> | null;

/** The multi-track timeline: clips with transitions, text/sticker/picture layers, music and voiceover. */
export function VideoTimeline({ ed, t, playing, onToggle, onSplit }: VideoTimelineProps) {
  const { doc, lay, total, sel } = ed;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(800);
  const [zoom, setZoom] = useState<number>(1);
  const drag = useRef<DragState>(null);
  const [ghost, setGhost] = useState<{ id: string; dx: number } | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const pps = ((width - 40) / Math.max(total, 1)) * zoom;
  const inner = Math.max(width, total * pps + 40);
  const X = (s: number) => 8 + s * pps;

  // keep the playhead in view while playing
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !playing) return;
    const x = X(t);
    if (x > el.scrollLeft + el.clientWidth - 40 || x < el.scrollLeft) el.scrollLeft = x - 60;
  });

  // ---------------- pointer helpers

  const startDrag = (e: React.PointerEvent, kind: string, data: Record<string, any>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { kind, x: e.clientX, moved: false, ...data };
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 3) d.moved = true;
    const ds = dx / pps;
    if (d.kind === 'seek') ed.seek(clamp((e.clientX - d.left + d.scroll) / pps - 8 / pps, 0, total));
    if (d.kind === 'trimA' || d.kind === 'trimB') {
      const c: Clip = d.clip;
      if (c.image) {
        const dur = clamp(c.duration + (d.kind === 'trimB' ? ds : -ds), 1, 15);
        ed.updateClip(c.id, { duration: r1(dur) }, false);
      } else if (d.kind === 'trimA') {
        ed.updateClip(c.id, { trimStart: r1(clamp(c.trimStart + ds * c.speed, 0, c.trimEnd - 0.5)) }, false);
      } else {
        ed.updateClip(c.id, { trimEnd: r1(clamp(c.trimEnd + ds * c.speed, c.trimStart + 0.5, c.sourceDuration || c.trimEnd + 600)) }, false);
      }
    }
    if (d.kind === 'move' && d.moved) setGhost({ id: d.clip.id, dx });
    if (d.kind === 'layer' || d.kind === 'layerA' || d.kind === 'layerB') {
      const l: VideoLayer = d.layer;
      let s = l.start;
      let en = l.end;
      if (d.kind === 'layer') { const len = en - s; s = clamp(s + ds, 0, total - len); en = s + len; }
      if (d.kind === 'layerA') s = clamp(s + ds, 0, en - 0.5);
      if (d.kind === 'layerB') en = clamp(en + ds, s + 0.5, total);
      ed.updateLayer(l.id, { start: r1(s), end: r1(en) }, false);
    }
    if (d.kind === 'audio' || d.kind === 'audioA' || d.kind === 'audioB') {
      const a: AudioItem = d.audio;
      if (d.kind === 'audio') {
        ed.updateAudio(a.id, { at: r1(clamp(a.at + ds, 0, Math.max(0, total - a.dur))) }, false);
      } else if (d.kind === 'audioA') {
        // move the start: the in-point moves with it, the end stays put
        const minShift = a.loop ? -a.at : Math.max(-a.at, -a.from);
        const shift = clamp(ds, minShift, a.dur - 0.2);
        const from = a.from + shift;
        ed.updateAudio(a.id, {
          at: r1(a.at + shift),
          dur: r1(a.dur - shift),
          from: a.loop && a.sourceDuration > 0 ? ((from % a.sourceDuration) + a.sourceDuration) % a.sourceDuration : from,
          toEnd: false,
        }, false);
      } else {
        const dur = clamp(a.dur + ds, 0.2, Math.min(total - a.at, audioMaxDur(a)));
        ed.updateAudio(a.id, { dur: r1(dur), toEnd: a.at + dur >= total - 0.05 }, false);
      }
    }
  };
  const onUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.kind === 'move') {
      setGhost(null);
      if (!d.moved) { ed.setSel({ type: 'clip', id: d.clip.id }); ed.setPanel('clip'); return; }
      // drop: the clip goes where its centre lands
      const k = doc.clips.findIndex((c) => c.id === d.clip.id);
      const it = lay.items[k];
      const centre = (it.start + it.end) / 2 + (e.clientX - d.x) / pps;
      let to = lay.items.findIndex((x) => centre < (x.start + x.end) / 2);
      if (to < 0) to = doc.clips.length - 1;
      else if (to > k) to -= 1;
      ed.moveClip(k, to);
      return;
    }
    if (d.kind === 'layer' && !d.moved) { ed.setSel({ type: 'layer', id: d.layer.id }); ed.setPanel(d.layer.kind === 'image' || d.layer.sticker ? 'elements' : 'text'); }
    if (d.kind === 'audio' && !d.moved) { ed.setSel({ type: 'audio', id: d.audio.id }); ed.setPanel(d.audio.role === 'voice' ? 'voice' : 'music'); }
    // commit the drag as one undo step
    ed.setDoc((x) => ({ ...x }), true);
  };

  const ticks: number[] = [];
  const step = pps > 60 ? 1 : pps > 25 ? 2 : pps > 10 ? 5 : 10;
  for (let s = 0; s <= total + 0.001; s += step) ticks.push(s);

  return (
    <section className="flex-none bg-white border-t border-st-line flex flex-col max-h-[42vh]" aria-label="Timeline">
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-st-line flex-wrap">
        <button
          className="w-[38px] h-[38px] inline-grid place-items-center rounded-[10px] border-0 bg-transparent text-st-muted cursor-pointer flex-none hover:bg-st-paper hover:text-st-ink disabled:opacity-35 disabled:cursor-not-allowed"
          onClick={onToggle}
          aria-label={playing ? 'Pause' : 'Play'}
          title="Play / pause (Space)"
        >
          <Icon name={playing ? 'pause' : 'play'} size={18} />
        </button>
        <span className="text-[13px] font-semibold tabular-nums">{fmt(t)} / {fmt(total)}</span>
        <button
          className="inline-flex items-center justify-center gap-2 min-h-[34px] px-[11px] py-[7px] rounded-[9px] border border-st-line bg-white text-st-ink font-semibold text-[13px] leading-none whitespace-nowrap cursor-pointer hover:border-[#9fb3ab]"
          onClick={onSplit}
          title="Split at the playhead (S)"
        >
          <Icon name="scissors" size={16} /> Split
        </button>
        <button
          className="inline-flex items-center justify-center gap-2 min-h-[34px] px-[11px] py-[7px] rounded-[9px] border border-st-line bg-white text-st-ink font-semibold text-[13px] leading-none whitespace-nowrap cursor-pointer hover:border-[#9fb3ab]"
          onClick={() => ed.setPanel('media')}
        >
          <Icon name="plus" size={16} /> Add clip
        </button>
        <div className="ml-auto flex items-center gap-1">
          <button
            className="w-[30px] h-[30px] inline-grid place-items-center rounded-lg border-0 bg-transparent text-st-muted cursor-pointer flex-none hover:bg-st-paper hover:text-st-ink"
            onClick={() => setZoom((z) => Math.max(1, z / 1.5))}
            aria-label="Zoom out timeline"
          >
            <Icon name="minus" size={14} />
          </button>
          <input
            className="w-[110px] accent-st-green"
            type="range"
            min={1}
            max={8}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Timeline zoom"
          />
          <button
            className="w-[30px] h-[30px] inline-grid place-items-center rounded-lg border-0 bg-transparent text-st-muted cursor-pointer flex-none hover:bg-st-paper hover:text-st-ink"
            onClick={() => setZoom((z) => Math.min(8, z * 1.5))}
            aria-label="Zoom in timeline"
          >
            <Icon name="plus" size={14} />
          </button>
        </div>
      </div>

      <div className="overflow-auto relative pb-2.5" ref={scrollRef} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        <div className="relative min-h-full" style={{ width: inner }}>
          {/* ruler */}
          <div
            className="relative h-6 cursor-pointer border-b border-st-line touch-none"
            onPointerDown={(e) => {
              const r = scrollRef.current!.getBoundingClientRect();
              startDrag(e, 'seek', { left: r.left, scroll: scrollRef.current!.scrollLeft });
              ed.seek(clamp((e.clientX - r.left + scrollRef.current!.scrollLeft - 8) / pps, 0, total));
            }}
          >
            {ticks.map((s) => (
              <span
                key={s}
                className="absolute top-1 text-[10.5px] text-st-muted tabular-nums pl-1 border-l border-[#c9d6d0] h-[18px] pointer-events-none"
                style={{ left: X(s) }}
              >
                {fmt(s).replace(/\.\d$/, '')}
              </span>
            ))}
          </div>

          {/* clips */}
          <div className="relative h-[62px] mt-2">
            {lay.items.map((it, k) => {
              const c = it.clip;
              const on = sel?.type === 'clip' && sel.id === c.id;
              const isGhost = ghost?.id === c.id;
              const w = Math.max(18, it.dur * pps - 2);
              return (
                <div
                  key={c.id}
                  className={[
                    'group absolute top-0 h-14 rounded-[10px] overflow-visible bg-[#1a2a25] cursor-grab touch-none',
                    'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] active:cursor-grabbing',
                    on ? 'shadow-[0_0_0_3px_#ffd166] z-[2]' : '',
                    isGhost ? 'opacity-75 z-[5] shadow-[0_12px_24px_-8px_rgba(0,0,0,0.5)]' : '',
                  ].filter(Boolean).join(' ')}
                  style={{
                    left: X(it.start) + 1,
                    width: w,
                    transform: isGhost ? `translateX(${ghost!.dx}px)` : undefined,
                  }}
                  onPointerDown={(e) => startDrag(e, 'move', { clip: c })}
                  title={`${c.name} · ${fmt(it.dur)}`}
                >
                  <ClipThumb clip={c} width={w} height={48} />
                  <span className="absolute left-1.5 bottom-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] bg-[rgba(14,28,24,0.78)] text-white text-[10.5px] font-semibold pointer-events-none whitespace-nowrap max-w-[calc(100%-12px)] overflow-hidden">
                    {c.image ? <Icon name="image" size={12} /> : <Icon name="video" size={12} />} {fmt(it.dur)}
                    {c.speed !== 1 && <em className="not-italic text-[#ffd166]">{c.speed}×</em>}
                    {c.filter !== 'NONE' && <em className="not-italic text-[#ffd166]">{CLIP_FILTERS.find((f) => f[0] === c.filter)?.[1]}</em>}
                    {c.muted && !c.image && <Icon name="mute" size={12} />}
                  </span>
                  <span
                    className={[
                      'absolute -left-0.5 top-0 bottom-0 w-2.5 cursor-ew-resize z-[3] grid place-items-center',
                      "after:content-[''] after:w-1 after:h-6 after:rounded-[3px] after:bg-[#ffd166] after:transition-opacity after:duration-100",
                      on ? 'after:opacity-100' : 'after:opacity-0 group-hover:after:opacity-100',
                    ].join(' ')}
                    onPointerDown={(e) => startDrag(e, 'trimA', { clip: c })}
                    aria-label="Trim start"
                    role="slider"
                    aria-valuenow={c.trimStart}
                  />
                  <span
                    className={[
                      'absolute -right-0.5 top-0 bottom-0 w-2.5 cursor-ew-resize z-[3] grid place-items-center',
                      "after:content-[''] after:w-1 after:h-6 after:rounded-[3px] after:bg-[#ffd166] after:transition-opacity after:duration-100",
                      on ? 'after:opacity-100' : 'after:opacity-0 group-hover:after:opacity-100',
                    ].join(' ')}
                    onPointerDown={(e) => startDrag(e, 'trimB', { clip: c })}
                    aria-label="Trim end"
                    role="slider"
                    aria-valuenow={c.trimEnd}
                  />
                  {k < lay.items.length - 1 && (
                    <button
                      className={[
                        'absolute -right-[13px] top-[15px] z-[4] w-[26px] h-[26px] rounded-lg border-2 border-white bg-st-ink text-white',
                        'text-sm font-bold leading-none cursor-pointer grid place-items-center shadow-[0_2px_6px_rgba(0,0,0,0.3)]',
                        c.transition !== 'NONE' ? 'bg-st-green' : '',
                        sel?.type === 'transition' && sel.id === c.id ? 'shadow-[0_0_0_3px_#ffd166]' : '',
                      ].filter(Boolean).join(' ')}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); ed.setSel({ type: 'transition', id: c.id }); ed.setPanel('transition'); }}
                      title={`Transition: ${TRANSITIONS.find((x) => x[0] === c.transition)?.[1]}`}
                      aria-label="Transition to the next clip"
                    >
                      {c.transition === 'NONE' ? '+' : '⇄'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* layers */}
          {doc.layers.map((l) => {
            const on = sel?.type === 'layer' && sel.id === l.id;
            const kindCls =
              l.kind === 'image'
                ? 'bg-gradient-to-r from-[#0369a1] to-[#0ea5e9]'
                : l.sticker
                  ? 'bg-gradient-to-r from-[#f97316] to-[#fb923c]'
                  : 'bg-gradient-to-r from-[#7c3aed] to-[#a855f7]';
            return (
              <div key={l.id} className="relative h-[30px] mt-1.5">
                <div
                  className={[
                    'absolute top-0 h-7 rounded-md text-white text-xs font-semibold flex items-center cursor-grab touch-none overflow-hidden',
                    kindCls,
                    on ? 'shadow-[0_0_0_2px_#10231e]' : '',
                  ].filter(Boolean).join(' ')}
                  style={{ left: X(l.start), width: Math.max(14, (l.end - l.start) * pps) }}
                  onPointerDown={(e) => startDrag(e, 'layer', { layer: l })}
                >
                  <span className="w-2.5 self-stretch cursor-ew-resize flex-none bg-white/[0.18]" onPointerDown={(e) => startDrag(e, 'layerA', { layer: l })} />
                  <span className="flex-1 min-w-0 px-1.5 whitespace-nowrap overflow-hidden text-ellipsis pointer-events-none">
                    {l.kind === 'image' ? '🖼 ' : l.sticker ? '' : 'T  '}{(l.text || '').split('\n')[0]}
                  </span>
                  <span className="w-2.5 self-stretch cursor-ew-resize flex-none bg-white/[0.18]" onPointerDown={(e) => startDrag(e, 'layerB', { layer: l })} />
                </div>
              </div>
            );
          })}

          {/* music and voiceover pieces, one lane each */}
          {doc.audios.map((a) => {
            const on = sel?.type === 'audio' && sel.id === a.id;
            const w = Math.max(16, a.dur * pps);
            const fin = Math.min(w / 2, a.fadeIn * pps);
            const fout = Math.min(w / 2, a.fadeOut * pps);
            return (
              <div key={a.id} className="relative h-[30px] mt-1.5">
                <div
                  className={[
                    'absolute top-0 h-7 rounded-md text-white text-xs font-semibold flex items-center cursor-grab touch-none overflow-hidden',
                    a.role === 'voice'
                      ? 'bg-[repeating-linear-gradient(90deg,#b7791f_0_3px,#d69e2e_3px_6px)]'
                      : 'bg-[repeating-linear-gradient(90deg,#0b7a5a_0_3px,#12936d_3px_6px)]',
                    on ? 'shadow-[0_0_0_2px_#10231e]' : '',
                  ].filter(Boolean).join(' ')}
                  style={{ left: X(a.at), width: w }}
                  onPointerDown={(e) => startDrag(e, 'audio', { audio: a })}
                  title={`${a.title} · ${fmt(a.dur)} — drag to move, drag the ends to trim, S to split`}
                >
                  {fin > 0 && (
                    <span
                      className="absolute left-0 top-0 h-full pointer-events-none bg-[linear-gradient(to_top_right,rgba(0,0,0,0.35)_50%,transparent_50%)]"
                      style={{ width: fin }}
                      aria-hidden="true"
                    />
                  )}
                  {fout > 0 && (
                    <span
                      className="absolute right-0 top-0 h-full pointer-events-none bg-[linear-gradient(to_top_left,rgba(0,0,0,0.35)_50%,transparent_50%)]"
                      style={{ width: fout }}
                      aria-hidden="true"
                    />
                  )}
                  <span className="relative w-2.5 self-stretch cursor-ew-resize flex-none bg-white/[0.18]" onPointerDown={(e) => startDrag(e, 'audioA', { audio: a })} aria-label="Trim start" />
                  <span className="relative flex-1 min-w-0 px-1.5 inline-flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis pointer-events-none">
                    <Icon name={a.role === 'voice' ? 'volume' : 'music'} size={12} /> {a.title}
                  </span>
                  <span className="relative w-2.5 self-stretch cursor-ew-resize flex-none bg-white/[0.18]" onPointerDown={(e) => startDrag(e, 'audioB', { audio: a })} aria-label="Trim end" />
                </div>
              </div>
            );
          })}

          <span className="absolute top-0 bottom-0 w-0.5 -ml-px bg-st-red z-[8] pointer-events-none" style={{ left: X(t) }} aria-hidden="true">
            <i className="absolute top-0 -left-[5px] w-3 h-3 rounded-tl-[3px] rounded-tr-[3px] rounded-bl-full rounded-br-full bg-st-red" />
          </span>
        </div>
      </div>
    </section>
  );
}